import { useEffect, useState } from "react";
import { Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { get, put, downloadCsv, inputCls, btnDark, Card, SectionTitle, StatusBadge, chf, monthKey, Empty } from "./shared";

const MONTHS_FR = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const monthLabel = (k) => `${MONTHS_FR[Number(k.slice(5, 7)) - 1]} ${k.slice(0, 4)}`;

export const Accounting = ({ settings, onSettings }) => {
  const [month, setMonth] = useState(monthKey());
  const [orders, setOrders] = useState(null);
  const [form, setForm] = useState({ vat_registered: settings?.vat_registered ?? false, vat_rate: settings?.vat_rate ?? 2.6, vat_number: settings?.vat_number ?? "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { setOrders(null); get("/orders", { month }).then(setOrders).catch(() => setOrders([])); }, [month]);

  const rate = Number(form.vat_rate) || 0;
  const valid = (orders || []).filter((o) => o.status !== "cancelled");
  const sum = valid.reduce((s, o) => s + (o.status === "refunded" ? -o.total : o.total), 0);
  const ht = sum / (1 + rate / 100);

  const download = async () => {
    setBusy(true);
    try { await downloadCsv(`/export/orders.csv?month=${month}`, `nura-commandes-${month}.csv`); toast.success(`Export ${monthLabel(month)} téléchargé`); }
    catch { toast.error("Export impossible"); }
    finally { setBusy(false); }
  };
  const saveSettings = async () => {
    try { const s = await put("/settings", { ...form, vat_rate: Number(form.vat_rate) }); onSettings(s); toast.success("Paramètres TVA enregistrés"); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-accounting">
      <Card className="p-6">
        <SectionTitle>Export mensuel des commandes (CSV)</SectionTitle>
        <p className="text-sm text-stone-600 leading-relaxed">
          Fichier CSV séparé par point-virgule, encodage UTF-8 (s'ouvre directement dans Excel / Numbers / Banana / Bexio en Suisse). Une ligne par commande avec HT, TVA et TTC, les remboursements en négatif et une ligne de total en fin de fichier.
        </p>
        <div className="mt-5 flex flex-wrap items-end gap-3">
          <label className="text-xs text-stone-500">Mois
            <input data-testid="accounting-month" type="month" value={month} max={monthKey()} onChange={(e) => setMonth(e.target.value)} className={`${inputCls} block mt-1`} />
          </label>
          <button data-testid="accounting-download" onClick={download} disabled={busy || !month} className={btnDark}><Download size={13} /> Télécharger {month && monthLabel(month)}</button>
        </div>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:col-span-2">
          <SectionTitle>Aperçu — {monthLabel(month)}</SectionTitle>
          {orders === null ? <Empty>Chargement…</Empty> : orders.length === 0 ? <Empty>Aucune commande sur ce mois.</Empty> : (
            <>
              <div className="grid grid-cols-3 gap-4 mb-5 text-sm">
                <div><p className="text-[10px] font-mono uppercase tracking-wide text-stone-400">Total TTC</p><p className="font-mono text-stone-900" data-testid="accounting-ttc">{chf(sum)}</p></div>
                <div><p className="text-[10px] font-mono uppercase tracking-wide text-stone-400">{form.vat_registered ? `dont TVA ${rate}%` : `TVA indicative ${rate}%`}</p><p className="font-mono text-stone-900">{chf(sum - ht)}</p></div>
                <div><p className="text-[10px] font-mono uppercase tracking-wide text-stone-400">Total HT</p><p className="font-mono text-stone-900">{chf(ht)}</p></div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[10px] font-mono uppercase tracking-wide text-stone-400 border-b border-stone-100"><th className="py-2 pr-3">Date</th><th className="py-2 pr-3">N°</th><th className="py-2 pr-3">Client</th><th className="py-2 pr-3">Statut</th><th className="py-2 text-right">TTC</th></tr></thead>
                  <tbody className="divide-y divide-stone-100">
                    {orders.map((o) => (
                      <tr key={o.id}>
                        <td className="py-2 pr-3 text-xs text-stone-500">{o.created_at.slice(0, 10)}</td>
                        <td className="py-2 pr-3 font-mono text-[#C85A32]">{o.order_number}</td>
                        <td className="py-2 pr-3 truncate max-w-[180px]">{o.customer_name || o.customer_email}</td>
                        <td className="py-2 pr-3"><StatusBadge status={o.status} /></td>
                        <td className={`py-2 text-right font-mono ${o.status === "refunded" ? "text-rose-700" : ""}`}>{o.status === "refunded" ? "−" : ""}{chf(o.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>

        <Card className="p-6" data-testid="vat-settings">
          <SectionTitle>Paramètres TVA</SectionTitle>
          <div className="space-y-4 text-sm">
            <label className="flex items-center gap-3">
              <input data-testid="vat-registered" type="checkbox" checked={form.vat_registered} onChange={(e) => setForm({ ...form, vat_registered: e.target.checked })} className="accent-[#C85A32] w-4 h-4" />
              <span>Entreprise assujettie à la TVA</span>
            </label>
            <label className="block text-xs text-stone-500">Taux appliqué (%)
              <input data-testid="vat-rate" type="number" step="0.1" min="0" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} className={`${inputCls} w-full mt-1`} />
            </label>
            <label className="block text-xs text-stone-500">N° TVA (CHE-xxx.xxx.xxx TVA)
              <input data-testid="vat-number" value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} placeholder="CHE-123.456.789 TVA" className={`${inputCls} w-full mt-1`} />
            </label>
            <button data-testid="vat-save" onClick={saveSettings} className={btnDark}><FileSpreadsheet size={13} /> Enregistrer</button>
            <p className="text-[11px] text-stone-400 leading-relaxed">
              En Suisse, les denrées alimentaires relèvent du taux réduit (2.6 % depuis 2024). L'assujettissement est obligatoire dès 100'000 CHF de chiffre d'affaires annuel. Vérifiez votre situation avec votre fiduciaire : cet outil calcule, il ne conseille pas.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};
